// apps/patient/src/pages/Signup.tsx

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { usePatient } from '../contexts/PatientContext';
import { patientApiService } from '../services/api';
import { getEnvironmentConfig } from '../config/platforms';
import { 
  Mail, 
  Phone, 
  CheckCircle,
  AlertCircle,
  Loader2
} from 'lucide-react';

type SignupStep = 'info' | 'success';

interface SignupFormData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  birthday: string;
}

export function SignupPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = usePatient();
  const config = getEnvironmentConfig();
  
  const [step, setStep] = useState<SignupStep>('info');
  const [formData, setFormData] = useState<SignupFormData>({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    birthday: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [partialMatch, setPartialMatch] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const updateFormData = (field: keyof SignupFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
    setPartialMatch(false);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
    updateFormData('phone', digits);
  };

  const formatPhoneDisplay = (phone: string): string => {
    if (phone.length <= 3) return phone;
    if (phone.length <= 6) return `(${phone.slice(0, 3)}) ${phone.slice(3)}`;
    return `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6)}`;
  };

  const validateForm = (): boolean => {
    if (!formData.first_name.trim()) {
      setError('First name is required');
      return false;
    }
    if (!formData.last_name.trim()) {
      setError('Last name is required');
      return false;
    }
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    if (!formData.phone || formData.phone.length !== 10) {
      setError('Please enter a valid 10-digit phone number');
      return false;
    }
    if (!formData.birthday) {
      setError('Date of birth is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (proceedAnyway = false) => {
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      const response = await patientApiService.createExerciseAccountPublic({
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone,
        birthday: formData.birthday,
        referral_code: sessionStorage.getItem('pending_referral_code') || undefined,
        routine_key: sessionStorage.getItem('pending_routine_key') || undefined,
        proceed_with_partial_match: proceedAnyway,
        solution_ids:[3]
      });

      if (response.success) {
        setStep('success');
        
        // Auto-redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login', {
            state: {
              signupSuccess: true,
              message: 'Account created! Please sign in with your email.',
              email: formData.email.trim().toLowerCase()
            },
            replace: true
          });
        }, 3000);
      } else if (response.match_type === 'exact') {
        setError('You already have an account. Please sign in instead.');
      } else if (response.match_type === 'partial' && !proceedAnyway) {
        setPartialMatch(true);
        setError('You might have an existing account under a different email or phone. If you think you have an account, please try signing in or contact support.');
      } else {
        setError(response.message || 'Failed to create account. Please try again.');
      }
    } catch (err) {
      console.error('Signup error:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Success screen
  if (step === 'success') {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-8">
        <div className="max-w-md w-full mx-auto px-4">
          <div className="bg-white py-8 px-6 shadow-md rounded-lg text-center">
            <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Account Created!
            </h1>
            
            <p className="text-gray-600 mb-6">
              Welcome to {config.appName}! You'll be redirected to sign in shortly.
            </p>
            
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Redirecting to login...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-8">
      <div className="max-w-md w-full mx-auto px-4">
        <div className="bg-white py-8 px-6 shadow-md rounded-lg">
          {/* Header */}
          <div className="text-center mb-6">
            <h2 
              className="text-3xl font-bold"
              style={{ color: config.themeColor }}
            >
              {config.appName}
            </h2>
            <p className="mt-2 text-gray-600">
              Create your free account
            </p>
          </div>

          {/* Form */}
          <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className="space-y-4">
            {/* Name fields */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name*
                </label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => updateFormData('first_name', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  placeholder="John"
                  disabled={loading}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name*
                </label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => updateFormData('last_name', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  placeholder="Doe"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address*
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => updateFormData('email', e.target.value)}
                  className="w-full p-3 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  placeholder="john@example.com"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number*
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="tel"
                  value={formatPhoneDisplay(formData.phone)}
                  onChange={handlePhoneChange}
                  className="w-full p-3 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  placeholder="(555) 123-4567"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Date of Birth */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date of Birth*
              </label>
              <input
                type="date"
                value={formData.birthday}
                onChange={(e) => updateFormData('birthday', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                disabled={loading}
              />
            </div>

            {/* Error message */}
            {error && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Partial match warning with proceed option */}
            {partialMatch && (
              <button
                type="button"
                onClick={() => handleSubmit(true)}
                className="w-full py-2 px-4 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100"
                disabled={loading}
              >
                Create new account anyway
              </button>
            )}

            {/* Submit button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 text-white rounded-lg font-medium transition-opacity hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50"
              style={{ backgroundColor: config.themeColor }}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Creating Account...
                </span>
              ) : (
                'Create Account'
              )}
            </button>
          </form>

          {/* Terms notice */}
          <p className="mt-4 text-center text-xs text-gray-500">
            By creating an account, you agree to our{' '}
            <a 
              href="https://ready2exercise.com/terms" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:underline"
              style={{ color: config.themeColor }}
            >
              Terms of Service
            </a>{' '}
            and{' '}
            <a 
              href="https://ready2exercise.com/privacy" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:underline"
              style={{ color: config.themeColor }}
            >
              Privacy Policy
            </a>
          </p>

          {/* Sign in link */}
          <div className="mt-6 pt-6 border-t text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link 
                to="/login" 
                className="font-medium hover:underline"
                style={{ color: config.themeColor }}
              >
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}